/**
 * Guest/bot account helpers.
 *
 * The `Guest_` username prefix is reserved server-side for guest accounts and
 * matchmaking bots (registered users are blocked from using it). So this is a
 * reliable, leak-free way to recognise unrated accounts on the client — it
 * treats real guests and disguised bots identically, keeping bots camouflaged.
 */

const GUEST_USERNAME_PREFIX = 'Guest_';

/** True when the username belongs to a guest or a bot (reserved `Guest_` prefix). */
export function isGuestUsername(username: string | null | undefined): boolean {
  return typeof username === 'string' && username.startsWith(GUEST_USERNAME_PREFIX);
}

/**
 * True when this player is a guest or a bot.
 *
 * On the wire a bot IS a guest (`OnlineUser` carries no `isBot` — the fleet stays
 * camouflaged), so the one flag answers for both. The `Guest_` prefix stays as the
 * net for a client on a bundle that predates the flag.
 *
 * Lives here rather than inside a component because two different screens need the
 * same answer: the connected-players list, and the game-type row that must not
 * offer a rated game to somebody who cannot move a rating.
 */
export function isGuestPlayer(user: { isGuest?: boolean; username: string }): boolean {
  return user.isGuest ?? isGuestUsername(user.username);
}
